import { useMemo } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { getPersonConflicts } from 'components/AbsenceRequestConflicts/utils'
import { useAbsenceRequestConflicts } from 'hooks/useAbsenceRequestConflicts'
import useHistoryUtils from 'hooks/useHistoryUtils'
import routes from 'services/RoutesProvider/routes'
import type { AbsenceRequestConflictsCardProps } from './types'

export const useAbsenceRequestConflictCard = ({
  startDate,
  endDate,
  isOffice = false,
  previewPerson: person,
  onClick,
  showAbsencePlannerPreview = false,
  setShowAbsencePlannerDrawer,
}: AbsenceRequestConflictsCardProps) => {
  const { pushWithRole } = useHistoryUtils()
  const { breakpoints } = useTheme()
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'))
  const previewTeams: string[] = useMemo(
    () => (person?.teamPermissions ?? []).flatMap((teamPermission) => teamPermission?.teamId ?? []),
    [person?.teamPermissions],
  )

  const { conflicts: allConflicts, loading } = useAbsenceRequestConflicts({
    startDate,
    endDate,
    isManager: isOffice,
    teamIds: previewTeams,
  })

  const handleClick = useMemo(() => {
    if (!showAbsencePlannerPreview) return onClick

    return () => {
      if (isSmallScreen) {
        setShowAbsencePlannerDrawer(true)
      } else {
        pushWithRole(`${routes.absencePlanner}?startDate=${startDate}&endDate=${endDate}`)
      }

      onClick?.()
    }
  }, [endDate, isSmallScreen, onClick, pushWithRole, setShowAbsencePlannerDrawer, showAbsencePlannerPreview, startDate])

  const conflicts = useMemo(() => {
    if (!allConflicts || !person) return allConflicts

    return getPersonConflicts(allConflicts, person?.id as unknown as number)
  }, [allConflicts, person])

  return { conflicts, loading: loading || !conflicts, handleClick }
}
